/**
 * Pure-data material profiles for the particle lab.
 *
 * G (heightfield material channel) is a height-weighted mix on 0..1:
 *   0.0 = brick, 0.5 = concrete, 1.0 = stone.
 * pileTint is sampled by lerping brick→concrete→stone along that axis.
 *
 * Grain sprites and deposited piles share one base colour per profile.
 * grainTint is a slightly darker / more saturated derivation so a dying
 * grain hands off to the pile without reading as a second substance.
 */

export type Rgb = readonly [number, number, number];

function clamp01(x: number): number {
  return Math.min(1, Math.max(0, x));
}

/** Darken + saturate a pile colour into a grain sprite tint (same hue). */
export function deriveGrainTint(base: Rgb): [number, number, number] {
  const luma = 0.2126 * base[0] + 0.7152 * base[1] + 0.0722 * base[2];
  const sat = 1.1;
  const dark = 0.92;
  return [
    clamp01(((base[0] - luma) * sat + luma) * dark),
    clamp01(((base[1] - luma) * sat + luma) * dark),
    clamp01(((base[2] - luma) * sat + luma) * dark),
  ];
}

const BRICK_TINT = [0.5, 0.22, 0.15] as const;
const CONCRETE_TINT = [0.5, 0.5, 0.48] as const;
const STONE_TINT = [0.27, 0.28, 0.3] as const;
const WOOD_TINT = [0.46, 0.3, 0.16] as const;
const GLASS_TINT = [0.55, 0.58, 0.57] as const;
const PLASTER_TINT = [0.76, 0.72, 0.64] as const;

// Crater-source clay is terracotta (crushed brick). Mortar stays grey.
const BRICK_CLAY = [0.46, 0.24, 0.16] as const;
const BRICK_MORTAR = [0.36, 0.34, 0.31] as const;
const CONCRETE_CLOUD = [0.38, 0.37, 0.34] as const;
const CONCRETE_CLOUD_B = [0.33, 0.32, 0.30] as const;
const STONE_CLOUD = [0.26, 0.28, 0.31] as const;
const WOOD_CLOUD = [0.5, 0.34, 0.18] as const;
const WOOD_CLOUD_B = [0.42, 0.28, 0.16] as const;
const GLASS_CLOUD = [0.7, 0.74, 0.73] as const;
const PLASTER_CLOUD = [0.8, 0.76, 0.68] as const;
const PLASTER_CLOUD_B = [0.72, 0.68, 0.6] as const;

export type ParticleProfileId = 'brick' | 'concrete' | 'stone' | 'wood' | 'glass' | 'plaster';

export interface ParticleMaterialProfile {
  readonly id: ParticleProfileId;
  readonly label: string;
  /** Linear RGB 0..1 grain colour. */
  readonly grainTint: readonly [number, number, number];
  /** Physical grain radius range (m). */
  readonly grainSizeRange: readonly [number, number];
  /** How many grains a 1.0 mass unit emits. */
  readonly grainsPerKg: number;
  readonly restitution: number;
  readonly friction: number;
  readonly reposeAngleDeg: number;
  /** Linear RGB 0..1 deposited-pile colour. */
  readonly pileTint: readonly [number, number, number];
  /** Encoded into heightfield G / R. */
  readonly materialId: number;
  /** Quadratic-ish drag coefficient (higher = finer dust slows faster). */
  readonly drag: number;
  /** Fraction of burst mass assigned to the gas/fines layer. */
  readonly fineFraction: number;
  /**
   * Cloud sprite tints. Brick has two populations (clay + mortar);
   * concrete/stone have one.
   */
  readonly cloudTints: readonly Rgb[];
  /** Upward mushroom acceleration scale (m/s²) near the burst core. */
  readonly cloudBuoyancy: number;
  /**
   * Main-population fine lifetime in seconds. Brick rain-out is short;
   * a haze subset uses `signature.hazeLinger`.
   */
  readonly cloudLinger: number;
  /** Impact-signature knobs. Zeroed fields keep the legacy path. */
  readonly signature: MaterialSignature;
}

/**
 * Per-material impact storyboard parameters. Concrete/stone ship zeros
 * so existing mushroom / linger behaviour is unchanged.
 */
export interface MaterialSignature {
  /** Fraction of burst mass assigned to whole-brick chunks. */
  readonly chunkFraction: number;
  /** Brick box extent (m): length, course, thickness. */
  readonly chunkExtent: readonly [number, number, number];
  readonly chunkSizeVariance: number;
  readonly chunkRestitution: number;
  readonly chunkFriction: number;
  readonly chunkHalfChance: number;
  readonly chunkCountRange: readonly [number, number];
  /** Launch speed scale vs the grain cone (chunks are slower). */
  readonly chunkVelScale: number;
  readonly chunkDownBias: number;
  readonly chunkStragglerRange: readonly [number, number];
  readonly chunkStragglerWindow: readonly [number, number];
  readonly chunkCap: number;
  /** Fraction of fines that keep a long thin haze instead of rain-out. */
  readonly hazeFraction: number;
  readonly hazeLinger: number;
  /** Age-based alpha exponent for the main cloud (higher = faster collapse). */
  readonly rainOutFadePower: number;
  /** Slump / settle acceleration scale (coarse grit falls faster). */
  readonly settleAccelScale: number;
  /**
   * Wall crater-jet half-angle range (radians). `null` keeps the legacy
   * 0.32 + energy*0.20 aperture.
   */
  readonly ejectionConeRad: readonly [number, number] | null;
  readonly cornerChipChance: number;
  readonly cornerChipScale: number;
  /** Crater-cone vs slower mortar-chip split (only when `sourceTintByFamily`). */
  readonly craterFamilyFrac: number;
  readonly mortarSpeedScale: number;
  readonly sourceTintByFamily: boolean;
  readonly jointCourse: number;
  readonly jointStretcher: number;
  readonly jointPointRange: readonly [number, number];
  readonly jointPuffWindow: readonly [number, number];
  readonly jointBleedWindow: readonly [number, number];
  readonly jointBleedPointRange: readonly [number, number];
  readonly jointPuffPerPoint: readonly [number, number];
  readonly jointBleedPerPoint: readonly [number, number];
  /**
   * Fraction of the planned grain *count* that stays on the GPGPU grit
   * stream. `1` keeps the legacy single population.
   */
  readonly gritFraction: number;
  /** Sand-scale size multiplier applied to `grainSizeRange` for grit. */
  readonly gritSizeScale: number;
  /** Grit dies at this age (s) even if still airborne. `8` is the legacy cap. */
  readonly gritMaxLife: number;
  /**
   * Visual height multiplier for dying-grain deposits. `1` is the legacy
   * pile. Ledger mass is unchanged — this is the documented visual/ledger
   * split.
   */
  readonly grainDepositScale: number;
  /** Fraction of planned grain count requested as angular chips. */
  readonly chipFraction: number;
  /** Hard cap on live chips (CPU instanced shards). `0` disables chips. */
  readonly chipCap: number;
  readonly chipRestitution: number;
  readonly chipMaxBounces: number;
  readonly chipPersistRange: readonly [number, number];
  readonly chipMortarChance: number;
}

export const EMPTY_SIGNATURE: MaterialSignature = {
  chunkFraction: 0,
  chunkExtent: [0.19, 0.09, 0.06],
  chunkSizeVariance: 0.15,
  chunkRestitution: 0.25,
  chunkFriction: 0.55,
  chunkHalfChance: 0.28,
  chunkCountRange: [0, 0],
  chunkVelScale: 0.35,
  chunkDownBias: 1.1,
  chunkStragglerRange: [0, 0],
  chunkStragglerWindow: [2, 3],
  chunkCap: 48,
  hazeFraction: 0,
  hazeLinger: 0,
  rainOutFadePower: 1.12,
  settleAccelScale: 1,
  ejectionConeRad: null,
  cornerChipChance: 0,
  cornerChipScale: 2.2,
  craterFamilyFrac: 0.6,
  mortarSpeedScale: 0.42,
  sourceTintByFamily: false,
  jointCourse: 0.2,
  jointStretcher: 0.4,
  jointPointRange: [0, 0],
  jointPuffWindow: [0.2, 0.6],
  jointBleedWindow: [2.0, 3.5],
  jointBleedPointRange: [0, 0],
  jointPuffPerPoint: [0, 0],
  jointBleedPerPoint: [0, 0],
  gritFraction: 1,
  gritSizeScale: 1,
  gritMaxLife: 8,
  grainDepositScale: 1,
  chipFraction: 0,
  chipCap: 0,
  chipRestitution: 0.3,
  chipMaxBounces: 2,
  chipPersistRange: [4, 6],
  chipMortarChance: 0.3,
};

export const BRICK_SIGNATURE: MaterialSignature = {
  chunkFraction: 0.25,
  chunkExtent: [0.19, 0.09, 0.06],
  chunkSizeVariance: 0.15,
  chunkRestitution: 0.25,
  chunkFriction: 0.55,
  chunkHalfChance: 0.28,
  chunkCountRange: [4, 14],
  chunkVelScale: 0.34,
  chunkDownBias: 1.25,
  chunkStragglerRange: [1, 3],
  chunkStragglerWindow: [2.0, 3.0],
  chunkCap: 48,
  hazeFraction: 0.1,
  hazeLinger: 6,
  rainOutFadePower: 2.45,
  settleAccelScale: 1.7,
  ejectionConeRad: [0.524, 0.785],
  cornerChipChance: 0.045,
  cornerChipScale: 2.35,
  craterFamilyFrac: 0.62,
  mortarSpeedScale: 0.4,
  sourceTintByFamily: true,
  jointCourse: 0.2,
  jointStretcher: 0.4,
  jointPointRange: [3, 6],
  jointPuffWindow: [0.2, 0.6],
  jointBleedWindow: [2.0, 3.5],
  jointBleedPointRange: [2, 4],
  jointPuffPerPoint: [4, 8],
  jointBleedPerPoint: [6, 12],
  gritFraction: 0.85,
  gritSizeScale: 0.14,
  gritMaxLife: 0.8,
  grainDepositScale: 0.07,
  chipFraction: 0.15,
  chipCap: 64,
  chipRestitution: 0.3,
  chipMaxBounces: 2,
  chipPersistRange: [4, 6],
  chipMortarChance: 0.3,
};

export const BRICK_PROFILE = {
  id: 'brick',
  label: 'Brick',
  grainTint: deriveGrainTint(BRICK_TINT),
  grainSizeRange: [0.007, 0.013],
  grainsPerKg: 2200,
  restitution: 0.16,
  friction: 0.58,
  reposeAngleDeg: 33,
  pileTint: BRICK_TINT,
  materialId: 0,
  drag: 0.55,
  fineFraction: 0.7,
  cloudTints: [BRICK_CLAY, BRICK_MORTAR],
  cloudBuoyancy: 2.6,
  cloudLinger: 1.8,
  signature: BRICK_SIGNATURE,
} as const satisfies ParticleMaterialProfile;

export const CONCRETE_PROFILE = {
  id: 'concrete',
  label: 'Concrete',
  grainTint: deriveGrainTint(CONCRETE_TINT),
  grainSizeRange: [0.011, 0.02],
  grainsPerKg: 1400,
  restitution: 0.22,
  friction: 0.5,
  reposeAngleDeg: 36,
  pileTint: CONCRETE_TINT,
  materialId: 0.5,
  drag: 0.38,
  fineFraction: 0.6,
  cloudTints: [CONCRETE_CLOUD, CONCRETE_CLOUD_B],
  cloudBuoyancy: 1.9,
  cloudLinger: 3.6,
  signature: EMPTY_SIGNATURE,
} as const satisfies ParticleMaterialProfile;

export const STONE_PROFILE = {
  id: 'stone',
  label: 'Stone',
  grainTint: deriveGrainTint(STONE_TINT),
  grainSizeRange: [0.018, 0.03],
  grainsPerKg: 700,
  restitution: 0.34,
  friction: 0.44,
  reposeAngleDeg: 40,
  pileTint: STONE_TINT,
  materialId: 1,
  drag: 0.22,
  fineFraction: 0.3,
  cloudTints: [STONE_CLOUD],
  cloudBuoyancy: 1.15,
  cloudLinger: 2.05,
  signature: EMPTY_SIGNATURE,
} as const satisfies ParticleMaterialProfile;

/** Fibrous warm-brown, low dust. Closest parent: brick (organic, warm). */
export const WOOD_PROFILE = {
  id: 'wood',
  label: 'Wood',
  grainTint: deriveGrainTint(WOOD_TINT),
  grainSizeRange: [0.008, 0.018],
  grainsPerKg: 900,
  restitution: 0.12,
  friction: 0.55,
  reposeAngleDeg: 32,
  pileTint: WOOD_TINT,
  materialId: 0.12,
  drag: 0.48,
  fineFraction: 0.22,
  cloudTints: [WOOD_CLOUD, WOOD_CLOUD_B],
  cloudBuoyancy: 1.35,
  cloudLinger: 1.4,
  signature: {
    ...EMPTY_SIGNATURE,
    gritFraction: 0.65,
    gritSizeScale: 0.45,
    gritMaxLife: 1.4,
    grainDepositScale: 0.35,
    hazeFraction: 0.04,
    hazeLinger: 2.4,
  },
} as const satisfies ParticleMaterialProfile;

/** Sparse glints, near-zero mineral dust. Closest parent: stone (hard, low dust). */
export const GLASS_PROFILE = {
  id: 'glass',
  label: 'Glass',
  grainTint: deriveGrainTint(GLASS_TINT),
  grainSizeRange: [0.004, 0.009],
  grainsPerKg: 180,
  restitution: 0.52,
  friction: 0.22,
  reposeAngleDeg: 28,
  pileTint: GLASS_TINT,
  materialId: 0.9,
  drag: 0.14,
  fineFraction: 0.08,
  cloudTints: [GLASS_CLOUD],
  cloudBuoyancy: 0.55,
  cloudLinger: 0.7,
  signature: {
    ...EMPTY_SIGNATURE,
    gritFraction: 0.35,
    gritSizeScale: 0.7,
    gritMaxLife: 0.9,
    grainDepositScale: 0.08,
  },
} as const satisfies ParticleMaterialProfile;

/** Bright fine powder, high dust. Closest parent: concrete (mineral powder). */
export const PLASTER_PROFILE = {
  id: 'plaster',
  label: 'Plaster',
  grainTint: deriveGrainTint(PLASTER_TINT),
  grainSizeRange: [0.004, 0.009],
  grainsPerKg: 2800,
  restitution: 0.08,
  friction: 0.62,
  reposeAngleDeg: 34,
  pileTint: PLASTER_TINT,
  materialId: 0.42,
  drag: 0.7,
  fineFraction: 0.88,
  cloudTints: [PLASTER_CLOUD, PLASTER_CLOUD_B],
  cloudBuoyancy: 2.35,
  cloudLinger: 4.0,
  signature: {
    ...EMPTY_SIGNATURE,
    hazeFraction: 0.22,
    hazeLinger: 5.5,
    gritFraction: 0.92,
    gritSizeScale: 0.18,
    gritMaxLife: 1.2,
    grainDepositScale: 0.55,
    rainOutFadePower: 1.05,
    settleAccelScale: 0.7,
  },
} as const satisfies ParticleMaterialProfile;

/** Lab UI catalog — brick/concrete/stone only so ParticleLab buttons stay unchanged. */
export const PARTICLE_PROFILES = [
  BRICK_PROFILE,
  CONCRETE_PROFILE,
  STONE_PROFILE,
] as const;

const ALL_PARTICLE_PROFILES: readonly ParticleMaterialProfile[] = [
  BRICK_PROFILE,
  CONCRETE_PROFILE,
  STONE_PROFILE,
  WOOD_PROFILE,
  GLASS_PROFILE,
  PLASTER_PROFILE,
];

export function getProfile(id: ParticleMaterialProfile['id']): ParticleMaterialProfile {
  const found = ALL_PARTICLE_PROFILES.find((p) => p.id === id);
  if (!found) throw new Error(`Unknown particle profile: ${id}`);
  return found;
}

/** Interpolate repose angle from the stored 0..1 material mix. */
export function reposeDegFromMix(mix: number): number {
  const t = Math.min(1, Math.max(0, mix));
  if (t < 0.5) {
    return BRICK_PROFILE.reposeAngleDeg
      + (CONCRETE_PROFILE.reposeAngleDeg - BRICK_PROFILE.reposeAngleDeg) * (t * 2);
  }
  return CONCRETE_PROFILE.reposeAngleDeg
    + (STONE_PROFILE.reposeAngleDeg - CONCRETE_PROFILE.reposeAngleDeg) * ((t - 0.5) * 2);
}

export function pileTintFromMix(mix: number): [number, number, number] {
  const t = Math.min(1, Math.max(0, mix));
  const a = t < 0.5 ? BRICK_PROFILE.pileTint : CONCRETE_PROFILE.pileTint;
  const b = t < 0.5 ? CONCRETE_PROFILE.pileTint : STONE_PROFILE.pileTint;
  const u = t < 0.5 ? t * 2 : (t - 0.5) * 2;
  return [
    a[0] + (b[0] - a[0]) * u,
    a[1] + (b[1] - a[1]) * u,
    a[2] + (b[2] - a[2]) * u,
  ];
}
